import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import NexovernLogo from '@/assets/nexovern-full.png';
import { useAuthStore } from '@/store/auth.store';
import { apiClient } from '@/lib/axios';
import { Button } from '@/components/ui';
import { SsoButtons } from '@/components/auth/SsoButtons';

// Deliberately loose — the backend is the real validator; this only catches
// obvious typos before a request is spent.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESET_COOLDOWN_SECONDS = 60;

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError, loginAttemptsRemaining, rateLimitRetryAfterSeconds } =
    useAuthStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const ssoErrorCode = searchParams.get('error');
  const ssoFailed = ssoErrorCode === 'sso_failed' || ssoErrorCode === 'no_account';
  const ssoNoAccountEmail = ssoErrorCode === 'no_account' ? searchParams.get('email') : null;

  const dismissSsoError = () => {
    searchParams.delete('error');
    searchParams.delete('email');
    setSearchParams(searchParams, { replace: true });
  };

  // Ticks down locally once the backend reports a lockout, so the message and the
  // disabled Sign in button update every second without polling the server.
  const [lockoutSeconds, setLockoutSeconds] = useState<number | null>(null);

  useEffect(() => {
    if (rateLimitRetryAfterSeconds != null) {
      setLockoutSeconds(rateLimitRetryAfterSeconds);
    }
  }, [rateLimitRetryAfterSeconds]);

  useEffect(() => {
    if (lockoutSeconds === null || lockoutSeconds <= 0) return;
    const timer = setTimeout(() => setLockoutSeconds((s) => (s === null ? null : s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [lockoutSeconds]);

  const isLockedOut = lockoutSeconds !== null && lockoutSeconds > 0;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password lives inline on the sign-in form: it reuses whatever is in
  // the email field instead of sending the user to a separate screen.
  const [resetEnabled, setResetEnabled] = useState(false);
  const [emailNote, setEmailNote] = useState<string | null>(null);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetNotice, setResetNotice] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetCooldown, setResetCooldown] = useState(0);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ enabled: boolean }>('/auth/password-reset/status')
      .then(({ data }) => {
        if (!cancelled) setResetEnabled(data.enabled);
      })
      .catch(() => {
        // Leave the link hidden — better than offering a flow that cannot email.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (resetCooldown <= 0) return;
    const timer = setTimeout(() => setResetCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resetCooldown]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isLockedOut) return;
    clearError();
    setEmailNote(null);
    setResetNotice(null);
    setResetError(null);
    // Drop the SSO banner once they try the password form, so a stale failure
    // from a previous attempt is not left sitting above a fresh error.
    if (ssoFailed) dismissSsoError();
    try {
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch {
      // Error is already set in the store
    }
  };

  const handleForgotPassword = async () => {
    clearError();
    setResetNotice(null);
    setResetError(null);

    const trimmed = email.trim();
    if (!trimmed) {
      setEmailNote('Please enter your email address to reset your password.');
      return;
    }
    if (!EMAIL_PATTERN.test(trimmed)) {
      setEmailNote('Please enter a valid email address.');
      return;
    }
    setEmailNote(null);

    setIsSendingReset(true);
    try {
      const { data } = await apiClient.post<{ message: string }>('/auth/forgot-password', {
        email: trimmed,
      });
      setResetNotice(data.message);
      setResetCooldown(RESET_COOLDOWN_SECONDS);
    } catch (err: unknown) {
      const response = (err as { response?: { status?: number; data?: { retryAfterSeconds?: number } } })
        ?.response;
      if (response?.status === 429) {
        const wait = response.data?.retryAfterSeconds ?? RESET_COOLDOWN_SECONDS;
        setResetError(`Too many reset requests. Please wait ${wait}s and try again.`);
        setResetCooldown(wait);
      } else {
        setResetError('Could not send a reset link right now. Please try again later.');
      }
    } finally {
      setIsSendingReset(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 flex items-center justify-center">
          <img src={NexovernLogo} alt="Nexovern" className="h-12 w-auto" />
        </div>

        {/* Card */}
        <div className="rounded-xl border border-border-subtle bg-surface-0 p-6 shadow-[var(--shadow-floating)]">
          <h2 className="mb-5 text-sm font-semibold text-text-primary">Sign in to your account</h2>

          {ssoFailed && (
            <div
              role="alert"
              className="mb-4 rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2 text-xs text-state-danger"
            >
              {ssoNoAccountEmail ? (
                <>
                  No Nexovern account is linked to{' '}
                  <span className="font-medium">{ssoNoAccountEmail}</span>. Contact your
                  administrator to request access.
                </>
              ) : (
                <>
                  We could not sign you in with that account. If you believe you should have
                  access, contact your administrator.
                </>
              )}
            </div>
          )}

          {isLockedOut ? (
            <div
              role="alert"
              className="mb-4 rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2 text-xs text-state-danger"
            >
              Too many login attempts. Please wait {lockoutSeconds}s before trying again.
            </div>
          ) : (
            error && (
              <div
                role="alert"
                className="mb-4 rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2 text-xs text-state-danger"
              >
                {error}
              </div>
            )
          )}

          {resetNotice && (
            <div
              role="status"
              className="mb-4 rounded-md border border-state-success/40 bg-state-success/10 px-3 py-2 text-xs text-state-success"
            >
              {resetNotice} Check your inbox — the link expires in 30 seconds.
            </div>
          )}

          {resetError && (
            <div
              role="alert"
              className="mb-4 rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2 text-xs text-state-danger"
            >
              {resetError}
            </div>
          )}

          {!isLockedOut && loginAttemptsRemaining !== null && loginAttemptsRemaining > 0 && (
            <p className="mb-4 -mt-2 text-xs text-text-tertiary">
              {loginAttemptsRemaining} login attempt{loginAttemptsRemaining === 1 ? '' : 's'} left.
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-text-secondary">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailNote) setEmailNote(null);
                }}
                aria-invalid={emailNote ? true : undefined}
                aria-describedby={emailNote ? 'email-note' : undefined}
                className={`w-full rounded-md border bg-surface-1 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20 ${
                  emailNote ? 'border-state-danger' : 'border-border-default'
                }`}
                placeholder="analyst@company.com"
              />
              {emailNote && (
                <p id="email-note" role="alert" className="mt-1.5 text-xs text-state-danger">
                  {emailNote}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="mb-1.5 block text-xs font-medium text-text-secondary">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 pr-10 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {resetEnabled && (
              <div className="-mt-1 text-right">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={isSendingReset || resetCooldown > 0}
                  className="text-xs font-medium text-accent hover:underline disabled:cursor-not-allowed disabled:text-text-tertiary disabled:no-underline"
                >
                  {isSendingReset
                    ? 'Sending reset link…'
                    : resetCooldown > 0
                      ? `Forgot password? (resend in ${resetCooldown}s)`
                      : 'Forgot password?'}
                </button>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="md"
              fullWidth
              loading={isLoading}
              disabled={isLockedOut}
            >
              {isLockedOut ? `Wait ${lockoutSeconds}s` : 'Sign in'}
            </Button>
          </form>

          <SsoButtons />

          <p className="mt-4 text-center text-[11px] text-text-tertiary">
            Contact your administrator to request access.
          </p>
        </div>
      </div>
    </div>
  );
}
